<template>
  <base-settings-page-sidebar
    :title="title"
    :actions-type="actionsType"
    :success-button-state="successButtonState"
    :cancel-button-state="cancelButtonState"
    :delete-button-state="deleteButtonState"
    @create="onSuccess('create')"
    @update="onSuccess('update')"
    @cancel="$emit('cancel')"
    @delete="onDelete"
  >
    <agent-status-item-settings
      :item="item"
      :errors="errors"
      @item-change="onItemChange"
    />

    <or-confirm
      ref="confirm"
      :title-text="$t('hitl.settings.agent-status.confirm-delete-status')"
      confirm-type="d"
      @confirm="onConfirmDelete"
    >
      {{ $t('hitl.settings.agent-status.confirm-delete-description') }}
    </or-confirm>
  </base-settings-page-sidebar>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent, PropType } from 'vue-demi';
import { OrConfirmV3 as OrConfirm } from '@onereach/ui-components-25';

import AgentStatusItemSettings from './AgentStatusItemSettings.vue';
import { BaseSettingsPageSidebar } from '@/modules/common-settings-ui';
import { AgentStatus } from '@/types';
import { checkIsKeyUnique } from '../common';
import { AGENT_STATUS_API } from '@/constants';

/**
 * Component manages own state and emits a copy of status to container.
 * TODO: At the moment only custom BUSY statuses are allowed!
 */
export default defineComponent({
  name: 'AgentStatusItemSidebar',
  components: {
    BaseSettingsPageSidebar,
    AgentStatusItemSettings,
    OrConfirm,
  },
  props: {
    actionsType: {
      type: String as PropType<'create' | 'update'>,
      default: 'create',
    },
    targetKey: {
      type: String,
      default: '',
    },
    statuses: {
      type: Object as PropType<Record<string, AgentStatus>>,
      default: () => ({}),
    },
    isUpdatingType: {
      type: String as PropType<'create' | 'update' | 'delete'>,
      default: '',
    },
  },
  emits: ['create', 'update', 'cancel', 'delete'],
  data() {
    return {
      item: {
        key: '',
        status: AGENT_STATUS_API.BUSY,
        icon: '',
        label: '',
        order: Object.values(this.statuses).length,
        expireIn: 60 * 3,
        canToggle: ['on', 'off'],
      } as AgentStatus,
      validate: {
        key: false,
        label: false,
      },
    };
  },
  computed: {
    title() {
      switch (this.actionsType) {
        case 'update':
          return this.$t('hitl.settings.agent-status.title-update-status');
        case 'create':
        default:
          return this.$t('hitl.settings.agent-status.title-create-status');
      }
    },

    isKeyUnique() {
      return checkIsKeyUnique({
        key: this.targetKey || this.item.key,
        newKey: this.targetKey ? this.item.key : '',
        items: this.statuses,
      });
    },

    isItemValid() {
      const isLabelValid = this.item.isBasic || !!this.item.label?.trim();
      return this.item.key && this.isKeyUnique && isLabelValid;
    },

    errors() {
      const key = this.validate.key && !this.item.key
        ? this.$t('hitl.settings.agent-status.error-property-empty')
        : this.validate.key && !this.isKeyUnique
          ? this.$t('hitl.settings.agent-status.error-key-not-unique')
          : undefined;

      const isLabelEmpty = !this.item.label?.trim();
      const label = this.validate.label && isLabelEmpty
        ? this.$t('hitl.settings.agent-status.error-property-empty')
        : undefined;
      return { key, label };
    },

    successButtonState() {
      switch (true) {
        case ['create', 'update'].includes(this.isUpdatingType):
          return 'loading';
        case !!this.isUpdatingType:
          return 'disabled';
        default:
          return '';
      }
    },

    cancelButtonState() {
      return this.isUpdatingType ? 'disabled' : '';
    },

    deleteButtonState() {
      switch (true) {
        case this.isUpdatingType === 'delete':
          return 'loading';
        case !!this.isUpdatingType:
        case this.item.isBasic:
          return 'disabled';
        default:
          return '';
      }
    },
  },
  watch: {
    /* Copy initial values when opening item for editing */
    targetKey: {
      handler(key: string) {
        if (key) {
          this.item = _.cloneDeep(this.statuses[key]);
          this.validate = { key: false, label: false };
        }
      },
      immediate: true,
    },
  },
  methods: {
    onItemChange(item: AgentStatus) {
      this.item = item;
      this.$emit('item-change', item);
    },

    onSuccess(event: 'create' | 'update') {
      this.isItemValid
        ? this.$emit(event, this.item)
        : (this.validate = { key: true, label: true });
    },

    onDelete() {
      (this.$refs['confirm'] as any).open();
    },

    onConfirmDelete() {
      (this.$refs['confirm'] as any).close();
      this.$emit('delete');
    },
  },
});
</script>
